import { FormEvent, useEffect, useState } from 'react';
import { LocationComboBox } from '@components/features/combobox/LocationComboBox';
import { FooterButtons } from '@components/templates/job/after-apply/FooterButtons';
import { useBjForm } from '@hooks/useBjForm';
import { useTranslation } from 'next-i18next';
import { useSwrStaticStudyLevels } from '@hooks/useSwrApi/endpoints/static';
import { subYears } from 'date-fns';
import {
  ComboBox, DatePickerInput, Input, Label, RadioText, Select,
} from '@components/common/Forms';

const MIN_DATE = subYears(new Date(), 65);
const MAX_DATE = new Date();

interface IProps {
  onSaveSuccess: () => void,
  locale: Locale,
}

/**
 * @description Experience form with: job title, company, startDate, endDate
 * @param onSaveSuccess - when form is submitted with success
 * @param onSkip - on skip form
 * @param locale - the current app locale
 */
export const Study = ({ onSaveSuccess, locale }: IProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  // state for datepicker
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentlyStudyingHere, setCurrentlyStudyingHere] = useState<boolean>(false);

  const {
    record, elementsRefs, formStatus, checkFormValidation, postFormData,
  } = useBjForm();

  useEffect(() => {
    if (formStatus.actionStatus === 'error') {
      setIsLoading(false);
    }
    if (formStatus.actionStatus === 'success') {
      onSaveSuccess();
      setIsLoading(false);
    }
  }, [onSaveSuccess, formStatus]);

  const onChangeCurrentlyStudyingHere = () => {
    if (!currentlyStudyingHere) {
      setEndDate(null);
    }
    setCurrentlyStudyingHere((prevState) => !prevState);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    event.preventDefault();
    const formData = checkFormValidation(event);
    if (typeof formData === 'object') {
      postFormData('/v1/resume/study', formData);
    } else {
      setIsLoading(false);
    }
  };

  const { data: studyLevels } = useSwrStaticStudyLevels(locale);

  return (
    <form noValidate onSubmit={onSubmit}>
      <div className="mb-5 grid gap-x-4 md:grid-cols-2">
        {/* type of studies field */}
        <Label description={t('study.level.label')} required>
          <Select
            {...record('studyLevel')}
            required
            customValidityMessages={{ valueMissing: t('error.messge.study.certificate') }}
          >
            <option value="">
              {t('study.level.placeholder')}
            </option>
            {
              studyLevels.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))
            }
          </Select>
        </Label>

        {/* institution name field */}
        <Label description={t('experience.institution.label')} required>
          <Input type="text" required {...record('institution')} customValidityMessages={{ valueMissing: t('error.message.study.institution') }} />
        </Label>

        {/* specialization field */}
        <Label description={t('study.faculty.label')}>
          <ComboBox
            {...record('specializationName', { removeFromSubmit: true })}
            options={{
              valueFromKey: 'text',
              freeSolo: false,
              apiList: {
                fetchUrl: '/v1/resume/study/specialization/autocomplete',
                fetcherConfig: {
                  headers: {
                    'Accept-Language': locale,
                  },
                },
                queryParams: (query: string) => ({ q: query }),
              },
            }}
            onSelectedItemChange={(item) => {
              elementsRefs.specialization.current?.setElementValue(item ? item['id' as keyof typeof item] : '');
            }}
          />
          <Input hidden type="text" {...record('specialization', { convertEmptyValue: 'toNull' })} />
        </Label>

        {/* location field */}
        <Label htmlFor="location" description={t('global.location.label')}>
          <LocationComboBox record={record} recordName="location" locale={locale} />
        </Label>

        <div className="mt-4">
          <Label htmlFor="currentlyStudyingHere">
            <Input
              type="checkbox"
              checked={currentlyStudyingHere}
              onChange={onChangeCurrentlyStudyingHere}
              id="currentlyStudyingHere"
              name="currentlyStudyingHere"
            />
            <RadioText>{t('cv-i-am-still-attending')}</RadioText>
          </Label>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">


            <Label description={t('study.admissionyear.label')} descriptionClass="w-full">
              <DatePickerInput
                {...record('admissionYear', { convertValue: 'toNumber' })}
                placeholder={t('global.year.placeholder')}
                date={startDate}
                setDate={setStartDate}
                pickerProps={{
                  minDate: MIN_DATE,
                  maxDate: endDate || MAX_DATE,
                }}
                pickerType="year"
              />
            </Label>

            {/* graduation year */}
            {
      !currentlyStudyingHere && (
        <Label description={t('study.graduationyear.label')} descriptionClass="w-full">
          <DatePickerInput
            {...record('graduationYear', { convertValue: 'toNumber' })}
            placeholder={t('global.year.placeholder')}
            date={endDate}
            setDate={setEndDate}
            pickerProps={{
              minDate: startDate || MIN_DATE,
              maxDate: new Date(),
            }}
            pickerType="year"
          />
        </Label>
      )
    }
          </div>

        </div>


      </div>

      {/* submit and skip buttons */}
      <FooterButtons
        submitType="submit"
        isLoading={isLoading}
      />
    </form>
  );
};
